// Raw Materials — a simple, visual, per-ingredient view of what's on site. Each material is one card
// (colour chip + total on-hand + nearest expiry); the single Edit button opens that material's lots to
// add / adjust / remove. Shared by the owner Raw Materials view and the canner portal.
import { useMemo, useRef, useState } from "react";
import type { StockLot } from "@shared/types.ts";
import { EmptyState, Modal, LabeledInput } from "./ui.tsx";
import { convertMass, isMassUom, UOM_OPTIONS } from "@shared/engine.ts";
import { fmtDate, mass, daysUntil } from "../lib/format.ts";

function matColor(s: string): string {
  let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xffff;
  return `hsl(${h % 360} 42% 46%)`;
}
function matInitials(s: string): string {
  const w = s.replace(/[^a-zA-Z ]/g, "").trim().split(/\s+/).filter(Boolean);
  return ((w[0]?.[0] || "") + (w[1]?.[0] || "")).toUpperCase() || "•";
}
const expiryClass = (dleft: number | null) => (dleft != null && dleft < 30 ? "text-danger" : dleft != null && dleft < 90 ? "text-warn" : "text-muted");

// Auto-group materials into readable buckets purely from the name — zero setup for the manufacturer.
const CAT_ORDER = ["Actives", "Flavourings", "Juices & concentrates", "Syrups", "Purées", "Sweeteners", "Acids", "Water & base", "Other"];
function matCategory(name: string): string {
  const n = name.toLowerCase();
  if (/thc|cbd|cannab|emulsion/.test(n)) return "Actives";
  if (/pur[eé]e/.test(n)) return "Purées";
  if (/syrup/.test(n)) return "Syrups";
  if (/juice|concentrate/.test(n)) return "Juices & concentrates";
  if (/flavour|flavor|wonf|taste sense/.test(n)) return "Flavourings";
  if (/acid|citric|ascorbic|malic|lactic/.test(n)) return "Acids";
  if (/sugar|stevia|sweeten|sucr|glucose|fructose|honey|erythritol/.test(n)) return "Sweeteners";
  if (/water|taurine/.test(n)) return "Water & base";
  return "Other";
}

export type BulkChange = { ingredient: string; lbs: number; lotId?: string; lotNumber?: string; expiryDate?: string; receivedDate?: string };

export function StockPanel({ ingredients, lots, busy, massUnit = "lb", onAdd, onUpdate, onDelete, onBulk, pill, title = "Raw Materials", subtitle = "What's on site right now, by material. This feeds the ingredient ordering calc." }: {
  ingredients: string[];
  lots: StockLot[];
  onHand: Record<string, number>; // (kept for API compatibility; the panel aggregates from lots)
  busy: boolean;
  massUnit?: string;
  onAdd: (lot: Partial<StockLot>) => void | Promise<void>;
  onUpdate: (lot: Partial<StockLot>) => void | Promise<void>;
  onDelete: (id: string) => void | Promise<void>;
  onBulk?: (changes: BulkChange[]) => void | Promise<void>;
  pill?: string;          // optional label chip above the title (e.g. section marker)
  title?: string;
  subtitle?: string;
}) {
  const [editing, setEditing] = useState<StockLot | null>(null);
  const [creating, setCreating] = useState<string | null>(null); // holds preset ingredient ("" = none)
  const [manage, setManage] = useState<string | null>(null);
  const [bulk, setBulk] = useState(false);

  const groups = useMemo(() => {
    const m = new Map<string, StockLot[]>();
    for (const l of lots) { if (!m.has(l.ingredient)) m.set(l.ingredient, []); m.get(l.ingredient)!.push(l); }
    return [...m.entries()].map(([ingredient, ls]) => ({
      ingredient,
      total: ls.reduce((a, l) => a + (Number(l.lbs) || 0), 0),
      lots: [...ls].sort((a, b) => (a.expiryDate || "").localeCompare(b.expiryDate || "")),
      expiry: ls.map((l) => l.expiryDate).filter(Boolean).sort()[0] || null,
    })).sort((a, b) => a.ingredient.localeCompare(b.ingredient));
  }, [lots]);

  const byCat = useMemo(() => {
    const m = new Map<string, typeof groups>();
    for (const g of groups) { const c = matCategory(g.ingredient); if (!m.has(c)) m.set(c, []); m.get(c)!.push(g); }
    return [...m.entries()].sort((a, b) => {
      const ia = CAT_ORDER.indexOf(a[0]), ib = CAT_ORDER.indexOf(b[0]);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
  }, [groups]);

  const manageGroup = manage ? groups.find((g) => g.ingredient === manage) : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          {pill && <span className="inline-block text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-ok/10 text-ok mb-1">{pill}</span>}
          <h2 className="text-sm font-semibold">{title}</h2>
          <p className="text-muted text-xs mt-0.5">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0 [&>button]:flex-1 sm:[&>button]:flex-none [&>button]:whitespace-nowrap">
          {onBulk && ingredients.length > 0 && <button className="btn-sm border border-edge hover:bg-paper" onClick={() => setBulk(true)}>Bulk edit</button>}
          <button className="btn-primary btn-sm" onClick={() => setCreating("")}>+ Add stock</button>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="card"><EmptyState title="No raw materials yet" sub="Add what's on site so orders subtract it automatically." /></div>
      ) : (
        <div className="space-y-5">
          {byCat.map(([cat, items]) => (
            <div key={cat} className="space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted">{cat} <span className="text-muted/60">· {items.length}</span></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((g) => {
                  const dleft = daysUntil(g.expiry);
                  return (
                    <div key={g.ingredient} className="card flex flex-col py-3.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="w-10 h-10 rounded-lg grid place-items-center text-white text-xs font-bold shrink-0" style={{ background: matColor(g.ingredient) }}>{matInitials(g.ingredient)}</div>
                        <button className="btn-sm border border-edge hover:bg-paper shrink-0" onClick={() => setManage(g.ingredient)}>Edit</button>
                      </div>
                      <div className="font-medium text-sm text-ink leading-snug break-words mt-2.5 min-h-[2.4rem]">{g.ingredient}</div>
                      <div className="text-2xl font-semibold tabular-nums leading-none mt-1">{mass(g.total, massUnit)}</div>
                      <div className="text-[11px] text-muted mt-1.5">{g.lots.length} lot{g.lots.length !== 1 ? "s" : ""}{g.expiry ? <> · <span className={expiryClass(dleft)}>exp {fmtDate(g.expiry)}</span></> : ""}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Manage one material's lots */}
      {manageGroup && (
        <Modal title={manageGroup.ingredient} onClose={() => setManage(null)}>
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm text-muted"><span className="font-semibold text-ink tabular-nums">{mass(manageGroup.total, massUnit)}</span> on hand · {manageGroup.lots.length} lot{manageGroup.lots.length !== 1 ? "s" : ""}</div>
              <button className="btn-sm border border-edge hover:bg-paper" onClick={() => setCreating(manageGroup.ingredient)}>+ Add lot</button>
            </div>
            <div className="divide-y divide-edge/60">
              {manageGroup.lots.map((l) => {
                const dl = daysUntil(l.expiryDate);
                return (
                  <div key={l.id} className="flex items-center gap-3 py-2.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium tabular-nums">{mass(l.lbs, massUnit)}</div>
                      <div className="text-[11px] text-muted">{l.lotNumber ? `Lot ${l.lotNumber}` : "No lot #"}{l.expiryDate ? <> · <span className={expiryClass(dl)}>exp {fmtDate(l.expiryDate)}</span></> : ""} · <span className="uppercase">{l.source}</span></div>
                    </div>
                    <button className="btn-sm border border-edge hover:bg-paper shrink-0" onClick={() => setEditing(l)}>Edit</button>
                  </div>
                );
              })}
            </div>
          </div>
        </Modal>
      )}

      {(creating !== null || editing) && (
        <LotModal
          ingredients={ingredients}
          lot={editing}
          presetIngredient={creating || undefined}
          busy={busy}
          massUnit={massUnit}
          onClose={() => { setCreating(null); setEditing(null); }}
          onSave={async (lot) => { editing ? await onUpdate({ ...lot, id: editing.id }) : await onAdd(lot); setCreating(null); setEditing(null); }}
          onDelete={editing ? async () => { await onDelete(editing.id); setEditing(null); } : undefined}
        />
      )}

      {bulk && onBulk && (
        <BulkModal ingredients={ingredients} groups={groups} massUnit={massUnit} busy={busy}
          onClose={() => setBulk(false)}
          onSave={async (changes) => { await onBulk(changes); setBulk(false); }} />
      )}
    </div>
  );
}

// Full stock-take: edit quantity, lot #, expiry and received date for every lot in one save — plus add
// new lots and delete existing ones. Each lot is a small card of labelled fields.
type FieldSet = { qty: string; lotNumber: string; expiry: string; received: string };

function BulkModal({ ingredients, groups, massUnit, busy, onClose, onSave }: {
  ingredients: string[];
  groups: { ingredient: string; total: number; lots: StockLot[] }[];
  massUnit: string; busy: boolean;
  onClose: () => void;
  onSave: (changes: BulkChange[]) => void | Promise<void>;
}) {
  const unitLabel = massUnit === "kg" ? "kg" : massUnit === "lb" ? "lb" : massUnit;
  const rnd = (n: number) => (n ? String(Math.round(n * 100) / 100) : "");
  const blank = (): FieldSet => ({ qty: "", lotNumber: "", expiry: "", received: "" });
  const fromLot = (l: StockLot): FieldSet => ({ qty: rnd(l.lbs), lotNumber: l.lotNumber || "", expiry: l.expiryDate || "", received: l.receivedDate || "" });
  const numOf = (s: string) => (s === "" || s == null ? 0 : Number(s));
  const differs = (a: FieldSet, b: FieldSet) => Math.abs(numOf(a.qty) - numOf(b.qty)) > 1e-6 || (a.lotNumber || "") !== (b.lotNumber || "") || (a.expiry || "") !== (b.expiry || "") || (a.received || "") !== (b.received || "");

  const materials = useMemo(() => ingredients
    .map((name) => ({ ingredient: name, lots: (groups.find((x) => x.ingredient === name)?.lots) || [] }))
    .sort((a, b) => a.ingredient.localeCompare(b.ingredient)), [ingredients, groups]);

  const [extra, setExtra] = useState<Record<string, string[]>>({}); // ingredient → keys of new (to-add) lots
  const [removed, setRemoved] = useState<Set<string>>(new Set());   // keys of existing lots marked for deletion
  const nextKey = useRef(0);
  const [fields, setFields] = useState<Record<string, FieldSet>>(() => {
    const f: Record<string, FieldSet> = {};
    for (const m of materials) {
      if (m.lots.length <= 1) f[m.ingredient] = m.lots[0] ? fromLot(m.lots[0]) : blank();
      else for (const l of m.lots) f[l.id] = fromLot(l);
    }
    return f;
  });

  // key → { ingredient, lotId (existing) , original values, isNew }
  const meta = useMemo(() => {
    const mm: Record<string, { ingredient: string; lotId?: string; orig: FieldSet; isNew?: boolean }> = {};
    for (const m of materials) {
      if (m.lots.length <= 1) mm[m.ingredient] = { ingredient: m.ingredient, lotId: m.lots[0]?.id, orig: m.lots[0] ? fromLot(m.lots[0]) : blank() };
      else for (const l of m.lots) mm[l.id] = { ingredient: m.ingredient, lotId: l.id, orig: fromLot(l) };
    }
    for (const [ing, keys] of Object.entries(extra)) for (const k of keys) mm[k] = { ingredient: ing, lotId: undefined, orig: blank(), isNew: true };
    return mm;
  }, [materials, extra]);

  const setField = (key: string, patch: Partial<FieldSet>) => setFields((s) => ({ ...s, [key]: { ...(s[key] || blank()), ...patch } }));
  const addLot = (ingredient: string) => {
    const k = `new_${nextKey.current++}`;
    setExtra((s) => ({ ...s, [ingredient]: [...(s[ingredient] || []), k] }));
    setFields((v) => ({ ...v, [k]: blank() }));
  };
  const removeRow = (key: string, isNew: boolean, ingredient: string) => {
    if (isNew) {
      setExtra((s) => ({ ...s, [ingredient]: (s[ingredient] || []).filter((k) => k !== key) }));
      setFields((v) => { const n = { ...v }; delete n[key]; return n; });
    } else {
      setRemoved((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
    }
  };

  const save = () => {
    const changes: BulkChange[] = [];
    for (const [key, m] of Object.entries(meta)) {
      if (removed.has(key)) { if (m.lotId) changes.push({ ingredient: m.ingredient, lbs: 0, lotId: m.lotId }); continue; }
      const f = fields[key] || blank();
      const changed = m.isNew ? numOf(f.qty) > 0 : differs(f, m.orig);
      if (!changed) continue;
      changes.push({ ingredient: m.ingredient, lbs: numOf(f.qty), lotId: m.lotId, lotNumber: f.lotNumber, expiryDate: f.expiry, receivedDate: f.received });
    }
    void onSave(changes);
  };

  const addLotBtn = (ingredient: string) => (
    <button type="button" className="text-[11px] text-brand hover:underline shrink-0" onClick={() => addLot(ingredient)}>+ add lot</button>
  );

  // One lot = a card of labelled fields (or a compact "removing" strip once marked for deletion).
  const lotCard = (key: string, label: string | null, opts?: { isNew?: boolean; ingredient?: string }) => {
    const f = fields[key] || blank();
    const canDelete = opts?.isNew || !!meta[key]?.lotId;
    if (removed.has(key)) return (
      <div key={key} className="flex items-center justify-between rounded-lg border border-danger/30 bg-danger/5 px-3 py-2">
        <span className="text-sm text-danger line-through">{label || "This lot"}</span>
        <button type="button" className="text-[11px] text-brand hover:underline" onClick={() => removeRow(key, false, "")}>undo remove</button>
      </div>
    );
    return (
      <div key={key} className="rounded-lg border border-edge/70 bg-paper/50 px-3 py-2.5">
        {(label || canDelete) && (
          <div className="flex items-center justify-between mb-1.5 min-h-[16px]">
            <span className="text-[11px] font-medium text-muted">{label || ""}</span>
            {canDelete && <button type="button" className="text-[11px] text-danger hover:underline" onClick={() => removeRow(key, !!opts?.isNew, opts?.ingredient || "")}>Delete</button>}
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <label className="block"><span className="label">Qty ({unitLabel})</span><input type="number" step="any" className="input py-1" value={f.qty} onChange={(e) => setField(key, { qty: e.target.value })} /></label>
          <label className="block"><span className="label">Lot #</span><input className="input py-1" value={f.lotNumber} onChange={(e) => setField(key, { lotNumber: e.target.value })} /></label>
          <label className="block"><span className="label">Expiry</span><input type="date" className="input py-1" value={f.expiry} onChange={(e) => setField(key, { expiry: e.target.value })} /></label>
          <label className="block"><span className="label">Received</span><input type="date" className="input py-1" value={f.received} onChange={(e) => setField(key, { received: e.target.value })} /></label>
        </div>
      </div>
    );
  };

  return (
    <Modal title="Bulk edit stock" onClose={onClose} wide>
      <div className="space-y-3">
        <p className="text-xs text-muted">Update quantity, lot #, expiry and received date for every material ({unitLabel}) in one go. “+ add lot” records a fresh delivery; Delete (or qty 0) removes a lot.</p>
        <div className="max-h-[65vh] overflow-y-auto -mx-1 px-1 space-y-3">
          {materials.map((m) => {
            const multi = m.lots.length > 1;
            const extras = extra[m.ingredient] || [];
            return (
              <div key={m.ingredient} className="pt-3 border-t border-edge/60 first:border-t-0 first:pt-0">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="text-sm font-semibold text-ink break-words">{m.ingredient}</div>
                  {addLotBtn(m.ingredient)}
                </div>
                <div className="space-y-2">
                  {multi
                    ? m.lots.map((l, i) => lotCard(l.id, l.lotNumber ? `Lot ${l.lotNumber}` : `Lot ${i + 1}`))
                    : lotCard(m.ingredient, null)}
                  {extras.map((k, i) => lotCard(k, `New lot ${i + 1}`, { isNew: true, ingredient: m.ingredient }))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary btn-sm" disabled={busy} onClick={save}>{busy ? "…" : "Save all"}</button>
        </div>
      </div>
    </Modal>
  );
}

function LotModal({ ingredients, lot, presetIngredient, busy, massUnit = "lb", onClose, onSave, onDelete }: {
  ingredients: string[]; lot: StockLot | null; presetIngredient?: string; busy: boolean; massUnit?: string;
  onClose: () => void; onSave: (lot: Partial<StockLot>) => void | Promise<void>; onDelete?: () => void | Promise<void>;
}) {
  const [ingredient, setIngredient] = useState(lot?.ingredient || presetIngredient || ingredients[0] || "");
  const [qty, setQty] = useState(lot ? String(lot.origQty ?? lot.lbs ?? "") : "");
  const [unit, setUnit] = useState(lot?.uom || massUnit);
  const [lotNumber, setLotNumber] = useState(lot?.lotNumber || "");
  const [expiryDate, setExpiry] = useState(lot?.expiryDate || "");
  const [receivedDate, setReceived] = useState(lot?.receivedDate || "");
  const [notes, setNotes] = useState(lot?.notes || "");

  return (
    <Modal title={lot ? "Edit stock lot" : "Add stock"} onClose={onClose}>
      <div className="space-y-3">
        <LabeledInput label="Material">
          <select className="input" value={ingredient} onChange={(e) => setIngredient(e.target.value)}>
            <option value="">— select material —</option>
            {ingredient && !ingredients.includes(ingredient) && <option value={ingredient}>{ingredient}</option>}
            {ingredients.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </LabeledInput>
        <div className="grid grid-cols-2 gap-3">
          <LabeledInput label="Quantity"><input type="number" step="any" className="input" value={qty} onChange={(e) => setQty(e.target.value)} /></LabeledInput>
          <LabeledInput label="Unit" hint="Converted to stock automatically">
            <select className="input" value={unit} onChange={(e) => setUnit(e.target.value)}>
              {unit && !UOM_OPTIONS.includes(unit) && <option value={unit}>{unit}</option>}
              {UOM_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </LabeledInput>
          <LabeledInput label="Lot #"><input className="input" value={lotNumber} onChange={(e) => setLotNumber(e.target.value)} /></LabeledInput>
          <LabeledInput label="Expiry date"><input type="date" className="input" value={expiryDate} onChange={(e) => setExpiry(e.target.value)} /></LabeledInput>
          <LabeledInput label="Received date"><input type="date" className="input" value={receivedDate} onChange={(e) => setReceived(e.target.value)} /></LabeledInput>
        </div>
        {isMassUom(unit) && unit.trim().toLowerCase() !== massUnit && (Number(qty) || 0) > 0 && (
          <p className="text-xs text-muted -mt-1">= <span className="font-medium text-ink">{mass(convertMass(Number(qty) || 0, unit, massUnit), massUnit)}</span> on hand</p>
        )}
        <LabeledInput label="Notes"><input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} /></LabeledInput>
        <div className="flex justify-between items-center pt-1">
          {onDelete ? <button className="btn-sm text-danger hover:bg-danger/5" disabled={busy} onClick={onDelete}>Delete</button> : <span />}
          <div className="flex gap-2">
            <button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn-primary btn-sm" disabled={busy || !ingredient} onClick={() => onSave({ ingredient, lbs: convertMass(Number(qty) || 0, unit, massUnit), origQty: Number(qty) || 0, uom: unit, lotNumber, expiryDate, receivedDate, notes })}>{busy ? "…" : "Save"}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
